import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Wallet, Plus, ArrowRight, Loader2 } from "lucide-react";

/**
 * Compact balance widget shown on dashboard + navbar.
 *
 * variant="card" (default) — full card with runway estimate + top-up CTA
 * variant="navbar" — single-line inline pill for the header
 */
export default function BalanceWidget({ variant = "card" }) {
  const [bal, setBal] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api
        .get("/balance/me")
        .then((r) => {
          if (!cancelled) setBal(r.data);
        })
        .catch(() => {
          if (!cancelled) setErr("error");
        });
    load();
    // Lightweight refresh — once a minute is enough since billing runs hourly
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (variant === "navbar") {
    return (
      <Link
        to="/topup"
        data-testid="navbar-balance"
        className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-300 hover:text-white px-3 py-1.5 border border-zinc-800 rounded-sm hover:border-zinc-600 transition-colors"
        title="Top up your CloudRent balance"
      >
        <Wallet size={13} className="text-signal" />
        {bal === null && !err ? (
          <Loader2 size={12} className="animate-spin" />
        ) : err ? (
          <span className="text-zinc-500">—</span>
        ) : (
          <span>${bal.balance_usd.toFixed(2)}</span>
        )}
      </Link>
    );
  }

  return (
    <div data-testid="balance-widget" className="card-flat p-6">
      <div className="flex items-center justify-between">
        <div className="overline">// ACCOUNT BALANCE</div>
        <Wallet size={16} className="text-signal" />
      </div>

      {bal === null && !err ? (
        <div className="mt-4 flex items-center gap-2 text-zinc-500">
          <Loader2 size={14} className="animate-spin" />
          <span className="font-mono text-xs uppercase tracking-widest">LOADING…</span>
        </div>
      ) : err ? (
        <div className="mt-4 font-mono text-xs text-zinc-500">// could not load</div>
      ) : (
        <>
          <div className="mt-3 flex items-baseline gap-1 font-mono">
            <span className="text-zinc-500 text-sm">$</span>
            <span className="text-4xl text-white font-semibold leading-none">
              {Math.floor(bal.balance_usd)}
            </span>
            <span className="text-zinc-500 text-sm">
              .{((bal.balance_usd % 1) * 100).toFixed(0).padStart(2, "0")}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-y-1 gap-x-4 font-mono text-[11px]">
            <div className="text-zinc-500 uppercase tracking-widest">DAILY BURN</div>
            <div className="text-zinc-200 text-right">${bal.daily_burn_usd.toFixed(2)}/d</div>
            <div className="text-zinc-500 uppercase tracking-widest">RUNWAY</div>
            <div className="text-zinc-200 text-right">
              {bal.runway_days == null ? "—" : `${bal.runway_days} days`}
            </div>
            <div className="text-zinc-500 uppercase tracking-widest">TOPPED UP</div>
            <div className="text-zinc-200 text-right">${bal.total_topped_up.toFixed(2)}</div>
            <div className="text-zinc-500 uppercase tracking-widest">CONSUMED</div>
            <div className="text-zinc-200 text-right">${bal.total_consumed.toFixed(2)}</div>
          </div>

          <Link
            to="/topup"
            data-testid="topup-cta"
            className="mt-5 btn-signal h-10 px-4 w-full text-xs font-mono tracking-widest"
          >
            <Plus size={14} /> TOP UP <ArrowRight size={12} />
          </Link>
        </>
      )}
    </div>
  );
}
